// Google Analytics 4 (GA4) tracking utilities
// E-commerce event tracking for add_to_cart, generate_lead, and purchase
import { logTrackingEvent } from '@/services/trackingService';
import { isTrackingEnabled } from '@/services/trackingSettingsService';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Helper to safely call gtag with tracking check
const safeGtag = async (command: string, eventName: string, params?: any) => {
  if (typeof window === 'undefined') return;
  
  // Check if tracking is enabled
  const trackingEnabled = await isTrackingEnabled();
  if (!trackingEnabled) {
    console.log(`GA4 ${eventName} skipped: tracking disabled`);
    return;
  }
  
  try {
    if (window.gtag) {
      window.gtag(command, eventName, params);
      console.log(`GA4 ${eventName} tracked:`, params);
    } else {
      // Queue the event if gtag is not ready yet
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push([command, eventName, params]);
      console.log(`GA4 ${eventName} queued:`, params);
    }
    
    // Log to PocketBase for admin tracking dashboard
    logTrackingEvent({
      event_type: `GA4_${eventName}`,
      event_id: params?.transaction_id || `${eventName}_${Date.now()}`,
      source: 'browser',
      metadata: { ...params, analytics_source: 'ga4' }
    });
  } catch (error) {
    console.error(`GA4 ${eventName} tracking error:`, error);
  }
};

/**
 * Track add_to_cart event (GA4 e-commerce)
 * @param product - Product details
 * @param quantity - Quantity added
 * @param extraMetadata - Optional extra metadata
 */
export const gaTrackAddToCart = (product: {
  id: string;
  title: string;
  price: number;
  category?: string;
}, quantity: number = 1, extraMetadata?: {
  tutor_name?: string;
  student_name?: string;
  student_email?: string;
  subject?: string;
}) => {
  safeGtag('event', 'add_to_cart', {
    currency: 'USD',
    value: product.price * quantity,
    items: [{
      item_id: product.id,
      item_name: extraMetadata?.tutor_name || product.title,
      item_category: product.category || 'Educational Products',
      price: product.price,
      quantity: quantity
    }],
    // Custom parameters for admin dashboard
    tutor_name: extraMetadata?.tutor_name,
    student_name: extraMetadata?.student_name,
    student_email: extraMetadata?.student_email,
    subject: extraMetadata?.subject
  });
};

/**
 * Track generate_lead event (GA4)
 * @param leadInfo - Lead details
 */
export const gaTrackLead = (leadInfo: {
  contentName: string;
  value?: number;
  currency?: string;
  tutor_name?: string;
  student_name?: string;
  student_email?: string;
  subject?: string;
}) => {
  safeGtag('event', 'generate_lead', {
    currency: leadInfo.currency || 'USD',
    value: leadInfo.value || 0,
    lead_source: leadInfo.contentName,
    // Custom parameters
    tutor_name: leadInfo.tutor_name,
    student_name: leadInfo.student_name,
    student_email: leadInfo.student_email,
    subject: leadInfo.subject
  });
};

/**
 * Track purchase event (GA4 e-commerce)
 * @param items - Cart items
 * @param totalValue - Total purchase amount
 * @param transactionId - Unique transaction ID
 * @param extraMetadata - Optional extra metadata
 */
export const gaTrackPurchase = (items: Array<{
  id: string;
  title: string;
  price: number;
  quantity: number;
}>, totalValue: number, transactionId?: string, extraMetadata?: {
  tutor_name?: string;
  student_name?: string;
  student_email?: string;
  subject?: string;
}) => {
  const gaItems = items.map(item => ({
    item_id: item.id,
    item_name: extraMetadata?.tutor_name || item.title,
    price: item.price,
    quantity: item.quantity
  }));

  safeGtag('event', 'purchase', {
    transaction_id: transactionId || `txn_${Date.now()}`,
    currency: 'USD',
    value: totalValue,
    items: gaItems,
    // Custom parameters for admin dashboard
    tutor_name: extraMetadata?.tutor_name,
    student_name: extraMetadata?.student_name,
    student_email: extraMetadata?.student_email,
    subject: extraMetadata?.subject
  });
};

/**
 * Track begin_checkout event (GA4 e-commerce)
 * @param items - Cart items
 * @param totalValue - Total checkout amount
 * @param extraMetadata - Optional extra metadata
 */
export const gaTrackBeginCheckout = (items: Array<{
  id: string;
  title: string;
  price: number;
  quantity: number;
}>, totalValue: number, extraMetadata?: {
  tutor_name?: string;
  student_name?: string;
  student_email?: string;
  subject?: string;
}) => {
  const gaItems = items.map(item => ({
    item_id: item.id,
    item_name: extraMetadata?.tutor_name || item.title,
    price: item.price,
    quantity: item.quantity
  }));

  safeGtag('event', 'begin_checkout', {
    currency: 'USD',
    value: totalValue,
    items: gaItems,
    // Custom parameters
    tutor_name: extraMetadata?.tutor_name,
    student_name: extraMetadata?.student_name,
    student_email: extraMetadata?.student_email,
    subject: extraMetadata?.subject
  });
};

/**
 * Track view_item event (GA4 e-commerce)
 * @param product - Product details
 */
export const gaTrackViewItem = (product: {
  id: string;
  title: string;
  price: number;
  category?: string;
}) => {
  safeGtag('event', 'view_item', {
    currency: 'USD',
    value: product.price,
    items: [{
      item_id: product.id,
      item_name: product.title,
      item_category: product.category || 'Educational Products',
      price: product.price
    }]
  });
};
